import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Building2, FileText, Settings, LogOut, Search, Trash2, ExternalLink,
  Loader2, ChevronRight, Crown, BarChart3, Calendar, Filter, HelpCircle, Calculator
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MyReportsPage = ({ auth }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports?limit=100`, {
        withCredentials: true
      });
      setReports(response.data);
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      await axios.delete(`${API}/reports/${reportToDelete}`, {
        withCredentials: true
      });
      setReports(reports.filter(r => r.report_id !== reportToDelete));
      toast.success("Report deleted");
    } catch (error) {
      toast.error("Failed to delete report");
    } finally {
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || report.property_type === filterType;
    return matchesSearch && matchesType;
  });

  const tierBadgeColor = {
    trial: "bg-slate-100 text-slate-700",
    free: "bg-slate-100 text-slate-700",
    basic: "bg-teal-100 text-teal-700",
    pro: "bg-purple-100 text-purple-700",
    admin: "bg-amber-100 text-amber-700"
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <BarChart3 className="h-5 w-5" />
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/reports" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white"
          >
            <FileText className="h-5 w-5" />
            <span>My Reports</span>
          </Link>
          <Link 
            to="/calculators" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Calculator className="h-5 w-5" />
            <span>Deal Calculators</span>
          </Link>
          <Link 
            to="/subscription" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Crown className="h-5 w-5" />
            <span>Subscription</span>
          </Link>
          <Link 
            to="/support" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <HelpCircle className="h-5 w-5" />
            <span>Support</span>
          </Link>
          <Link 
            to="/settings" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-teal-500 text-navy-900">
                  {auth.user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                <Badge className={`${tierBadgeColor[auth.user?.subscription_tier || "free"]} text-xs`}>
                  {auth.user?.subscription_tier?.toUpperCase() || "FREE"}
                </Badge>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
              onClick={auth.logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <BarChart3 className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")}>
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900 mb-2">
              My Reports
            </h1>
            <p className="text-slate-600">
              View and manage your rent analysis reports
            </p>
          </div>

          {/* Filters */}
          <Card className="mb-6" data-testid="reports-filters">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by address or city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="filter-type">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Property Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="single_family">Single Family</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                    <SelectItem value="duplex">Duplex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Reports List */}
          <Card data-testid="reports-list">
            <CardHeader>
              <CardTitle className="font-heading flex items-center justify-between">
                <span className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-teal-500" />
                  {filteredReports.length} Report{filteredReports.length !== 1 ? "s" : ""}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  {searchQuery || filterType !== "all" ? (
                    <p>No reports match your filters</p>
                  ) : (
                    <>
                      <p>No reports yet</p>
                      <Button 
                        onClick={() => navigate("/dashboard")} 
                        className="mt-4 bg-teal-500 hover:bg-teal-400 text-navy-900"
                      >
                        Create Your First Report
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredReports.map((report) => (
                    <div 
                      key={report.report_id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
                      data-testid={`report-row-${report.report_id}`}
                    >
                      <Link 
                        to={`/report/${report.report_id}`}
                        className="flex-1 min-w-0"
                      >
                        <p className="font-medium text-navy-900 truncate">{report.address}</p>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <span>{report.city}, {report.state}</span>
                          <span>•</span>
                          <span>{report.bedrooms} BR</span>
                          <span>•</span>
                          <span className="capitalize">{report.property_type.replace("_", " ")}</span>
                        </div>
                      </Link>
                      <div className="flex items-center gap-4 ml-4">
                        <div className="text-right hidden sm:block">
                          <p className="font-data font-semibold text-navy-900">
                            ${report.estimated_rent?.toLocaleString()}/mo
                          </p>
                          <p className="text-xs text-slate-500 flex items-center justify-end">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(report.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate(`/report/${report.report_id}`)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.preventDefault();
                              setReportToDelete(report.report_id);
                              setDeleteDialogOpen(true);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-btn-${report.report_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the report and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyReportsPage;
